import * as React from 'react';

import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import FormSection from 'components/ReportRequestV2/FormSection';
import ParentReportPicker from 'components/ReportRequestV2/ParentReportPicker';

import { StepProps } from './types';
import { ParentReportRow } from '../hooks/useParentReports';

export interface ParentDetailsStatus {
  loading: boolean;
  error?: { message: string };
  missing: boolean;
}

interface Props extends StepProps {
  reports: ParentReportRow[];
  reportsLoading: boolean;
  reportsError?: { message: string };
  details: ParentDetailsStatus;
}

const formatAges = (ranges: { min: number; max: number; fraction: number }[]) =>
  ranges.map((r) => `${r.min}–${r.max} (${Math.round(r.fraction * 100)}%)`).join(', ');

const ParentStep: React.FC<Props> = ({
  state,
  dispatch,
  errors,
  reports,
  reportsLoading,
  reportsError,
  details,
}) => {
  const isMixAndMatch = state.reportKind === 'mix_and_match';
  const { inherited } = state;

  return (
    <>
      <FormSection
        title="Report to build on"
        description="The new report inherits the parent's demographics and competitive set."
      >
        <ParentReportPicker
          reports={reports}
          selectedJobId={state.parent?.jobId ?? null}
          onSelect={(option) => {
            const parent = reports.find((r) => r.jobId === option.jobId) ?? null;
            if (parent?.jobId !== state.parent?.jobId) dispatch({ type: 'selectParent', parent });
          }}
          loading={reportsLoading}
          error={reportsError ? `Reports failed to load: ${reportsError.message}` : errors.parent}
          helperText={
            isMixAndMatch
              ? 'Only reports with at least one optimization run are listed.'
              : undefined
          }
        />
      </FormSection>

      {state.parent && (
        <FormSection title="Inherited from the parent">
          {details.loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading the parent report parameters…</Typography>
            </Stack>
          )}
          {details.error && (
            <Alert severity="error">The parent parameters failed to load: {details.error.message}</Alert>
          )}
          {details.missing && (
            <Alert severity="error">
              This report has no stored parameters and cannot be used as a parent.
            </Alert>
          )}
          {inherited && !details.loading && (
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                columnGap: 3,
                rowGap: 1,
                m: 0,
                '& dt': { color: 'text.secondary' },
                '& dd': { m: 0 },
              }}
            >
              <dt>Countries</dt>
              <dd>{inherited.countries.join(', ').toUpperCase() || '—'}</dd>
              <dt>Ages</dt>
              <dd>{formatAges(inherited.ageRanges) || '—'}</dd>
              <dt>Gender</dt>
              <dd>
                {Math.round(inherited.includeFemale * 100)}% female /{' '}
                {Math.round(inherited.includeMale * 100)}% male
              </dd>
              <dt>Categories</dt>
              <dd>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {inherited.competitiveSetFolders.map((c) => (
                    <Chip key={c} size="small" label={c} />
                  ))}
                </Stack>
              </dd>
              <dt>Products</dt>
              <dd>{inherited.competitiveSetProducts.length}</dd>
            </Box>
          )}
        </FormSection>
      )}
    </>
  );
};

export default ParentStep;
